import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button
} from '@mui/material';
import { 
  BarChart, 
  Assessment, 
  TrendingUp, 
  Speed,
  ArrowForward
} from '@mui/icons-material';

interface DashboardsPageProps {
  fontFamily?: string;
  onNavigateToEnGateway: () => void;
  onNavigateToIce: () => void;
}

export const DashboardsPage: React.FC<DashboardsPageProps> = ({ 
  fontFamily = 'JetBrains Mono', 
  onNavigateToEnGateway,
  onNavigateToIce
}) => {
  const dashboards = [
    {
      id: 'engateway',
      title: 'EN Gateway Dashboard',
      description: 'Monitor API performance, success rates, and third-party integrations for the EN Gateway service.',
      icon: <BarChart sx={{ fontSize: 48, color: 'primary.main' }} />,
      status: 'active',
      lastUpdated: 'Updated daily',
      metrics: ['API Success Rate', 'Failure Analysis', 'SurePass & Digitap Performance'],
      onClick: onNavigateToEnGateway,
      available: true
    },
    {
      id: 'ice',
      title: 'ICE Vehicle Lookup Dashboard',
      description: 'Monitor vehicle lookup performance, success rates, and response time analytics.',
      icon: <Assessment sx={{ fontSize: 48, color: 'secondary.main' }} />,
      status: 'active',
      lastUpdated: 'Updated daily',
      metrics: ['Success Rate', 'Response Time', 'Status Code Analysis'],
      onClick: onNavigateToIce,
      available: true
    }
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily, mb: 3 }}>
        🚀 Available Dashboards
      </Typography>
      
      <Typography variant="body1" sx={{ fontFamily, mb: 4, color: 'text.secondary' }}>
        Select a dashboard to view detailed analytics and performance metrics.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {dashboards.map((dashboard) => (
          <Box key={dashboard.id}>
            <Card 
              elevation={3}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: dashboard.available ? 'translateY(-4px)' : 'none',
                  boxShadow: dashboard.available ? 6 : 3,
                  cursor: dashboard.available ? 'pointer' : 'default'
                },
                opacity: dashboard.available ? 1 : 0.7
              }}
              onClick={dashboard.available ? dashboard.onClick : undefined}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {dashboard.icon}
                  <Box sx={{ ml: 2 }}>
                    <Typography variant="h5" component="h2" sx={{ fontFamily, fontWeight: 'bold' }}>
                      {dashboard.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: dashboard.available ? 'success.main' : 'warning.main',
                          mr: 1
                        }}
                      />
                      <Typography variant="caption" sx={{ fontFamily, color: 'text.secondary' }}>
                        {dashboard.lastUpdated}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ fontFamily, mb: 3, color: 'text.secondary' }}>
                  {dashboard.description}
                </Typography>

                <Typography variant="subtitle2" sx={{ fontFamily, fontWeight: 'bold', mb: 1 }}>
                  📊 Key Metrics:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {dashboard.metrics.map((metric, index) => (
                    <Typography 
                      key={index} 
                      component="li" 
                      variant="body2" 
                      sx={{ fontFamily, mb: 0.5, color: 'text.secondary' }}
                    >
                      {metric}
                    </Typography>
                  ))}
                </Box>
              </CardContent>

              <CardActions sx={{ p: 3, pt: 0 }}>
                <Button
                  variant={dashboard.available ? "contained" : "outlined"}
                  endIcon={dashboard.available ? <ArrowForward /> : <Speed />}
                  onClick={dashboard.available ? dashboard.onClick : undefined}
                  disabled={!dashboard.available}
                  sx={{ fontFamily }}
                  fullWidth
                >
                  {dashboard.available ? 'Open Dashboard' : 'Coming Soon'}
                </Button>
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      <Paper elevation={1} sx={{ mt: 4, p: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" sx={{ fontFamily, mb: 2 }}>
          💡 Dashboard Features
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontFamily, fontWeight: 'bold' }}>
                Real-time Analytics
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily, color: 'text.secondary' }}>
              Live data updates and performance monitoring
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Speed sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontFamily, fontWeight: 'bold' }}>
                Performance Metrics
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily, color: 'text.secondary' }}>
              Detailed insights into system performance
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Assessment sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontFamily, fontWeight: 'bold' }}>
                Interactive Charts
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontFamily, color: 'text.secondary' }}>
              Visual data representation and analysis tools
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}; 